<template>
  <v-card dark color="primary">
    <v-card-text>
      <v-row justify="space-between" no-gutters>
        <v-col>
          <v-text-field
            :value="hp"
            label="HP"
            class="secondary"
            outlined
            readonly
            hide-details />
        </v-col>
        <v-col cols="1" />
        <v-col>
          <v-text-field
            :value="ac"
            label="AC"
            class="secondary"
            outlined
            readonly
            hide-details />
        </v-col>
        <v-col cols="1" />
        <v-col>
          <v-text-field
            :value="dr"
            label="DR"
            class="secondary"
            outlined
            readonly
            hide-details />
        </v-col>
      </v-row>
      <hr style="height:10px; visibility:hidden;" />
      <v-row justify="space-between" no-gutters>
        <v-col>
          <v-text-field
            :value="actions"
            label="Actions"
            class="secondary"
            outlined
            readonly
            hide-details />
        </v-col>
        <v-col cols="1" />
        <v-col>
          <v-text-field
            :value="stamina"
            label="Stamina"
            class="secondary"
            outlined
            readonly
            hide-details />
        </v-col>
      </v-row>
      <hr style="height:10px; visibility:hidden;" />
      <v-row justify="space-between" no-gutters>
        <v-col>
          <v-text-field
            :value="speed"
            label="Speed"
            class="secondary"
            outlined
            readonly
            hide-details />
        </v-col>
        <v-col cols="1" />
        <v-col>
          <v-text-field
            :value="mana"
            label="Mana"
            class="secondary"
            outlined
            readonly
            hide-details />
        </v-col>
      </v-row>
      <hr style="height:10px; visibility:hidden;" />
      <v-row justify="space-between" no-gutters>
        <v-col>
          <v-text-field
            :value="carryingCapacity"
            label="Carrying Capacity"
            class="secondary"
            outlined
            readonly
            hide-details />
        </v-col>
        <v-col cols="1" />
        <v-col>
          <v-text-field
            :value="encumbrance ? 'Yes' : 'No'"
            label="Encumbered"
            class="secondary"
            outlined
            readonly
            hide-details />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';

export default {
  props: {
    character: {
      type: Object,
      required: true
    }
  },
  methods: {
    traitCounter(findTrait) {
      return this.character.traits.filter(trait => trait.includes(findTrait)).length;
    }
  },
  computed: {
    hp() {
      let hp = 0;
      this.character.hpRolls.forEach(roll => hp += roll );
      hp += parseInt(this.character.race.hp, 10) * this.character.level;

      if (this.character.traits.includes('Toughness')) {
        hp += this.character.level;
      }
      if (this.character.traits.includes('Improved Toughness')) {
        hp += this.character.level * 2;
      }
      if (this.character.traits.includes('Sticks and Stones')) {
        hp += this.character.mind;
      }

      return hp;
    },
    speed() {
      let speed = 2;
      if (this.character.finesse > 3) {
        speed += 1;
      }
      if (this.character.finesse > 7) {
        speed += 1;
      }
      if (this.character.finesse > 12) {
        speed += 1;
      }
      if (this.character.finesse > 18) {
        speed += 1;
      }
      if (this.character.traits.includes('Fleet Feet')) {
        speed += 1;
      }
      if (this.character.race.other.includes('+1 Speed')) {
        speed += 1;
      }
      if (this.character.race.other.includes('-1 Speed')) {
        speed -= 1;
      }

      return speed;
    },
    ac() {
      let armor = 10 + Number(this.character.equippedArmor.ac);
      if (this.character.equippedArmor.type === 'Unarmored') {
        if (!this.encumbrance) {
          if (!this.character.equippedOff.ac) {
            armor += this.traitCounter('Self Defense') * 2;
          }
          if (this.character.traits.includes('Protect Self')) {
            armor += Math.floor(this.character.spirit /2 );
          }
        } 
      } else {
        armor += this.traitCounter('Armor Proficiency');
      }
      if (this.character.equippedOff.ac) {
        armor += Number(this.character.equippedOff.ac);
        armor += this.traitCounter('Shield Proficiency') * 2;
      }
      if (!this.encumbrance) {
        armor += Math.floor(this.character.finesse / 2);
        if (this.character.traits.includes('Evasive Duelist') && !this.offHandDisabled && (this.character.equippedMain.name === 'Unarmed' || this.character.equippedOff.name === 'Unarmed')) {
          armor += Math.floor(this.character.mind /2);
        }
      }
      if (this.character.race.other.includes('+1 AC')) {
        armor += 1;
      }
      if (this.character.race.other.includes('-1 AC')) {
        armor -= 1;
      }
      return armor;
    },
    stamina() {
      let statValues = [];
      this.character.primaryStats.forEach(stat => statValues.push(this.character[stat.toLowerCase()]));
      statValues.sort();
      let stamina = statValues[0] + statValues[1];
      stamina += this.traitCounter('Spiritual Boost') * 2;
      return stamina;
    },
    actions() {
      let actionCount = 2;
      if (this.character.traits.includes('Rapid Reflexes')) {
        actionCount++;
      }
      if (this.character.mind > 4) {
        actionCount++;
      }
      if (this.character.mind > 9) {
        actionCount++;
      }
      if (this.character.mind > 15) {
        actionCount++;
      }
      return actionCount;
    },
    mana() {
      let mana = 1;
      if (this.character.spirit > 4) {
        mana += 1;
      }
      if (this.character.spirit > 9) {
        mana += 1;
      }
      if (this.character.spirit > 15) {
        mana += 1;
      }
      mana += Math.floor((this.character.spirit + this.character.mind + this.character.level) / 2);
      mana += this.traitCounter('(Spell)');
      return mana;
    },
    carryingCapacity() {
      return this.character.physique * 20;
    },
    encumbrance() {
      let armorCheck = 0;
      if (this.character.equippedArmor.type === 'Heavy') {
        armorCheck = 3;
      } else if (this.character.equippedArmor.type === 'Medium') {
        armorCheck = 2;
      } else if (this.character.equippedArmor.type === 'Light') {
        armorCheck = 1;
      }

      if (this.character.equippedOff.class === 'Shield') {
        armorCheck += 1;
      }

      const armorAllowance = Math.ceil(this.character.physique / 3) - 1;

      if (armorCheck > armorAllowance) {
        return true;
      }

      let calculatedWeight = Number(this.character.equippedOff.weight) + Number(this.character.equippedMain.weight) + Number(this.character.equippedArmor.weight);
      // this.character.inventory.forEach(item => calculatedWeight += item.weight);
      if (this.character.traits.includes('Puny Weapon')) {
        calculatedWeight -= this.character.equippedMain.weight;
        calculatedWeight -= this.character.equippedOff.weight;
      }
      if (calculatedWeight > this.carryingCapacity) {
        return true;
      }
      return false;
    },
    dr() {
      let dr = 0;
      if (this.character.equippedOff.features && this.character.equippedOff.features.includes('Protective')) {
        dr += 2;
      }
      dr += (this.traitCounter('Resistor') + this.traitCounter('Improved Resistance')) * 2;
      return dr;
    }
  }
};
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: white;
  border-width: 1px;
}
</style>
